import { useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * "The Maple Leaf Advantage" horizontal accordion. Mirrors the
 * `.hoz-accordion` markup from design-source/index.html lines 979-1098.
 *
 * Five inline icons map to feature index 0..4 — these are the design's
 * fixed iconography, NOT taken from CMS data. The Feature model only
 * supplies title + description (and an image, fallback below).
 */
const ICONS = [
  // 1. Continuous Learning Model — bookshelf
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 4v16M19 4v16M5 8h14M5 16h14M9 12h6"/></svg>',
  // 2. Community Focused — people
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="3"/><circle cx="5" cy="10" r="2.2"/><circle cx="19" cy="10" r="2.2"/><path d="M6 20c0-3 2.5-5 6-5s6 2 6 5M2 20c0-2 1.5-3.5 3.5-3.5M22 20c0-2-1.5-3.5-3.5-3.5"/></svg>',
  // 3. More Than a Teacher — chalkboard + person
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="11" rx="1"/><path d="M7 8h10M7 11h6"/><circle cx="12" cy="18" r="2.2"/><path d="M8 22c0-2 2-3 4-3s4 1 4 3"/></svg>',
  // 4. Education Awards — medal
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="9" r="5"/><path d="M9 13l-2 8 5-3 5 3-2-8M5 6c-1 4 .5 7 3 8M19 6c1 4-.5 7-3 8"/></svg>',
  // 5. Academic Success — open book
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M4 5c2-1 5-1 7 0v14c-2-1-5-1-7 0V5zM20 5c-2-1-5-1-7 0v14c2-1 5-1 7 0V5zM12 5v14"/></svg>',
];

const FALLBACK_IMAGES = [
  'https://images.unsplash.com/photo-1577896851231-70ef18881754?w=1400&q=80',
  'https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=1400&q=80',
  'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1400&q=80',
  'https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=1400&q=80',
  'https://images.unsplash.com/photo-1497486751825-1233686d5d80?w=1400&q=80',
];

const FALLBACK_FEATURES = [
  { id: 'f1', title: 'Continuous Learning Model', description: 'Our continuous learning pathway delivers teaching in a way that recognises the unique social, emotional and academic needs of each student — from Play Group right through to A-Levels.', readMore: '/page/igcse-ial-info' },
  { id: 'f2', title: 'Community Focused',         description: 'Maple Leaf is, above all, an integrated community of students, teachers, parents and alumni — working together to ensure that the school remains a supportive and enriching environment in which to learn and grow.', readMore: '/alumni' },
  { id: 'f3', title: 'More Than a Teacher',       description: 'Our dedicated faculty go beyond the classroom — mentoring, guiding and inspiring every Maple Leaf student to discover their strengths, build resilience and grow into confident young adults.', readMore: '/page/principals-message' },
  { id: 'f4', title: 'Education Awards',          description: 'Maple Leaf has earned national recognition and a host of respected awards — the result of a consistent combination of innovation, dedicated educators and outstanding student outcomes.', readMore: '/achievements' },
  { id: 'f5', title: 'Academic Success',          description: 'Maple Leaf is acclaimed for its outstanding academic results — and for the unique combination of factors that, together, deliver more than the sum of their parts.', readMore: '/page/career-counseling-university-placement' },
];

export function FeatureGrid({ features }) {
  const list = (features && features.length) ? features : FALLBACK_FEATURES;
  const [active, setActive] = useState(0);
  // Mobile only: which stacked card is expanded — null = all collapsed (default),
  // one open at a time. Desktop keeps the horizontal `active` accordion.
  const [mobileOpen, setMobileOpen] = useState(null);

  const handleClick = (i) => {
    if (typeof window !== 'undefined' && window.matchMedia?.('(max-width: 860px)').matches) {
      setMobileOpen((prev) => (prev === i ? null : i));
    } else {
      setActive(i);
    }
  };

  return (
    <div className="hoz-accordion reveal" data-hoz-accordion>
      <ul>
        {list.map((f, i) => {
          const icon = ICONS[i % ICONS.length];
          const image = f.image || FALLBACK_IMAGES[i % FALLBACK_IMAGES.length];
          const readMore = f.readMore || FALLBACK_FEATURES[i]?.readMore;
          return (
            <li
              key={f.id ?? i}
              className={`${i === active ? 'is-active' : ''}${i !== mobileOpen ? ' is-collapsed' : ''}`.trim()}
              data-tab
              onClick={() => handleClick(i)}
              onMouseEnter={() => setActive(i)}
            >
              <div className="img-outer">
                <div className="img-c" style={{ backgroundImage: `url('${image}')` }} />
              </div>
              <div className="accordion-c">
                <div className="tab-title">
                  <span>{f.title}</span>
                  <i className="hoz-icon" aria-hidden="true" dangerouslySetInnerHTML={{ __html: icon }} />
                </div>
                <div className="accordion-content">
                  <i className="hoz-icon-lg" aria-hidden="true" dangerouslySetInnerHTML={{ __html: icon }} />
                  <h3>{f.title}</h3>
                  <p>{f.description}</p>
                  {readMore && (
                    <p className="link">
                      {readMore.startsWith('/')
                        ? <Link to={readMore}>Read More</Link>
                        : <a href={readMore}>Read More</a>}
                    </p>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
